import { useCallback, useEffect, useRef } from 'react';
import { useDebounce } from '../../../core/utils/hooks/useDebounce';

const SCROLL_OFFSET = 80;

const getScrollAnchors = (container: HTMLElement | null) => {
    if (!container) return [];
    return Array.from(container.querySelectorAll('[data-scroll-anchor]'));
};

// Chapters collapse via clipping (grid-rows-[0fr] + overflow-hidden in Chapter.Content),
// so hidden position cards keep their natural bounding rects. Intersect the element's
// rect with every overflow-clipping ancestor up to the scroll container to get the
// actually visible vertical extent. Returns null when fully clipped.
const getClippedRect = (element: Element, container: HTMLElement): { top: number; bottom: number } | null => {
    const rect = element.getBoundingClientRect();
    let top = rect.top;
    let bottom = rect.bottom;

    let parent = element.parentElement;
    while (parent && parent !== container) {
        if (window.getComputedStyle(parent).overflowY !== 'visible') {
            const parentRect = parent.getBoundingClientRect();
            top = Math.max(top, parentRect.top);
            bottom = Math.min(bottom, parentRect.bottom);
            if (bottom - top < 1) return null;
        }
        parent = parent.parentElement;
    }

    return { top, bottom };
};

const getClosestScrollAnchorIndex = (container: HTMLElement | null) => {
    const anchors = getScrollAnchors(container);
    if (!anchors.length || !container) return 0;

    const containerTop = container.getBoundingClientRect().top;
    let closestIndex = 0;
    let minDistance = Infinity;

    anchors.forEach((anchor, index) => {
        const clippedRect = getClippedRect(anchor, container);
        if (!clippedRect) return; // Hidden inside a collapsed chapter

        const distance = Math.abs(clippedRect.top - containerTop);

        if (distance < minDistance) {
            minDistance = distance;
            closestIndex = index;
        }
    });

    return closestIndex;
};

const getTopVisibleRelevantPosition = (container: HTMLElement | null): string | null => {
    if (!container) return null;

    const allPositions = Array.from(container.querySelectorAll('[data-position-index]'));

    if (!allPositions.length) return null;

    const containerRect = container.getBoundingClientRect();
    const containerTop = containerRect.top + SCROLL_OFFSET;

    let topPosition: Element | null = null;
    let minDistance = Infinity;

    allPositions.forEach((position) => {
        const positionRect = getClippedRect(position, container);
        if (!positionRect) return; // Hidden inside a collapsed chapter

        if (positionRect.bottom < containerTop || positionRect.top > containerRect.bottom) return;

        const distance = Math.abs(positionRect.top - containerTop);

        if (distance < minDistance) {
            minDistance = distance;
            topPosition = position;
        }
    });

    if (!topPosition) return null;

    const relevantAnchor = topPosition.querySelector('[data-scroll-anchor="position"]');

    if (!relevantAnchor) return null; // Position is not relevant

    const anchorRect = getClippedRect(relevantAnchor, container);
    if (!anchorRect || anchorRect.bottom - anchorRect.top < 10) return null; // Position is collapsed

    return topPosition.getAttribute('data-position-index');
};

export const useScrollSync = (
    onScrollAnchorIndexChange: (index: number, source?: 'positions' | 'articles') => void,
    autoScrollEnabled: boolean
) => {
    const positionsScrollRef = useRef<HTMLDivElement | null>(null);
    const autoScrollEnabledRef = useRef(autoScrollEnabled);
    const prevAutoScrollEnabledRef = useRef(autoScrollEnabled);
    const articlesScrollRef = useRef<HTMLDivElement | null>(null);
    const lastSyncedPositionRef = useRef<string | null>(null);
    const isUserScrollingArticlesRef = useRef(false);
    const isNavigatingRef = useRef(false);
    const navigationTimeoutRef = useRef<number | null>(null);
    const scrollTimeoutRef = useRef<number | null>(null);
    const autoScrollDelayRef = useRef<number | null>(null);
    const scrollToPositionOverrideRef = useRef<((positionIndex: string) => void) | null>(null);
    const isProgrammaticScrollRef = useRef(false);
    const programmaticScrollTimeoutRef = useRef<number | null>(null);

    const setProgrammaticScroll = useCallback((active: boolean) => {
        if (programmaticScrollTimeoutRef.current) {
            window.clearTimeout(programmaticScrollTimeoutRef.current);
            programmaticScrollTimeoutRef.current = null;
        }
        isProgrammaticScrollRef.current = active;
        if (active) {
            programmaticScrollTimeoutRef.current = window.setTimeout(() => {
                isProgrammaticScrollRef.current = false;
                programmaticScrollTimeoutRef.current = null;
            }, 500);
        }
    }, []);

    const suppressAutoSync = useCallback((ms = 2000) => {
        isNavigatingRef.current = true;
        if (autoScrollDelayRef.current) {
            window.clearTimeout(autoScrollDelayRef.current);
            autoScrollDelayRef.current = null;
        }
        if (navigationTimeoutRef.current) {
            window.clearTimeout(navigationTimeoutRef.current);
        }
        navigationTimeoutRef.current = window.setTimeout(() => {
            isNavigatingRef.current = false;
        }, ms);
    }, []);

    const scrollArticlesToPosition = useCallback(
        (positionIndex: string) => {
            if (scrollToPositionOverrideRef.current) {
                setProgrammaticScroll(true);
                scrollToPositionOverrideRef.current(positionIndex);
                return;
            }

            const articlesContainer = articlesScrollRef.current;
            if (!articlesContainer) return;

            const targetElement = articlesContainer.querySelector(`[data-position-container="${positionIndex}"]`);

            if (targetElement) {
                const targetRect = targetElement.getBoundingClientRect();
                const containerRect = articlesContainer.getBoundingClientRect();

                setProgrammaticScroll(true);
                articlesContainer.scrollTo({
                    top: targetRect.top - containerRect.top + articlesContainer.scrollTop - SCROLL_OFFSET,
                    behavior: 'smooth',
                });
            }
        },
        [setProgrammaticScroll]
    );

    const handleScrollPositions = useCallback(() => {
        if (isUserScrollingArticlesRef.current || isNavigatingRef.current) return;

        if (autoScrollEnabledRef.current) {
            const positionIndex = getTopVisibleRelevantPosition(positionsScrollRef.current);

            // If no relevant positions are visible (positionIndex is null), don't scroll the right panel
            if (positionIndex) {
                if (positionIndex !== lastSyncedPositionRef.current) {
                    lastSyncedPositionRef.current = positionIndex;

                    if (autoScrollDelayRef.current) {
                        window.clearTimeout(autoScrollDelayRef.current);
                    }

                    autoScrollDelayRef.current = window.setTimeout(() => {
                        scrollArticlesToPosition(positionIndex);
                    }, 150);
                }
            } else {
                lastSyncedPositionRef.current = null;

                // Clear any pending auto-scroll when no relevant positions are visible
                if (autoScrollDelayRef.current) {
                    window.clearTimeout(autoScrollDelayRef.current);
                    autoScrollDelayRef.current = null;
                }
            }
        }

        const closestChildIndex = getClosestScrollAnchorIndex(positionsScrollRef.current);
        onScrollAnchorIndexChange(closestChildIndex, 'positions');
    }, [onScrollAnchorIndexChange, scrollArticlesToPosition]);

    // Keep the flag readable from stable callbacks, and handle toggle transitions:
    // off — cancel any pending auto-follow scroll; on — re-sync immediately instead of
    // waiting for the next scroll event.
    useEffect(() => {
        autoScrollEnabledRef.current = autoScrollEnabled;
        if (prevAutoScrollEnabledRef.current === autoScrollEnabled) return;
        prevAutoScrollEnabledRef.current = autoScrollEnabled;

        if (!autoScrollEnabled) {
            if (autoScrollDelayRef.current) {
                window.clearTimeout(autoScrollDelayRef.current);
                autoScrollDelayRef.current = null;
            }
        } else {
            lastSyncedPositionRef.current = null;
            handleScrollPositions();
        }
    }, [autoScrollEnabled, handleScrollPositions]);

    const handleScrollArticles = useCallback(() => {
        if (!isProgrammaticScrollRef.current) {
            isUserScrollingArticlesRef.current = true;

            if (scrollTimeoutRef.current) {
                window.clearTimeout(scrollTimeoutRef.current);
            }

            scrollTimeoutRef.current = window.setTimeout(() => {
                isUserScrollingArticlesRef.current = false;
            }, 400);
        }

        const closestChildIndex = getClosestScrollAnchorIndex(articlesScrollRef.current);
        onScrollAnchorIndexChange(closestChildIndex, 'articles');
    }, [onScrollAnchorIndexChange]);

    const debouncedHandleScrollPositions = useDebounce(handleScrollPositions, 150);
    const debouncedHandleScrollArticles = useDebounce(handleScrollArticles, 300);

    // Reset lastSyncedPositionRef when the positions panel's scroll content height changes
    // (e.g., positions expanded/collapsed). This allows re-syncing to the same position
    // after layout changes. The existing debounce + delay naturally throttle re-sync frequency.
    useEffect(() => {
        const scrollEl = positionsScrollRef.current;
        if (!scrollEl) return;

        const contentEl = scrollEl.firstElementChild;
        if (!contentEl) return;

        const observer = new ResizeObserver(() => {
            lastSyncedPositionRef.current = null;
        });
        observer.observe(contentEl);
        return () => observer.disconnect();
    }, []);

    return {
        positionsScrollRef,
        articlesScrollRef,
        debouncedHandleScrollPositions,
        debouncedHandleScrollArticles,
        getScrollAnchors,
        scrollArticlesToPosition,
        scrollToPositionOverrideRef,
        suppressAutoSync,
        setProgrammaticScroll,
    };
};
